<template>
  <v-container fluid class="pa-0 login-container">
    <v-overlay :value="isLoading" absolute>
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular indeterminate size="64" :width="5" />
        <span class="my-4">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <RegisterShop
      v-if="stage === 'welcome'"
      @start="goInfo"
      @back="handleBackFromWelcome"
    />

    <div v-else-if="stage === 'info'" class="stage-wrapper">
      <Stepper :current-step="1" @step-click="jumpToStep" />
      <RegisterInfo
        :value="infoForm"
        @next="handleInfoNext"
        @back="stage = 'welcome'"
      />
    </div>

    <div v-else-if="stage === 'iden'" class="stage-wrapper">
      <Stepper :current-step="2" @step-click="jumpToStep" />
      <RegisterIdenCard :value="idenForm" @back="stage = 'info'" @next="handleIdenNext" />
    </div>

    <div v-else-if="stage === 'documents'" class="stage-wrapper">
      <Stepper :current-step="3" @step-click="jumpToStep" />
      <RegisterDocuments
        :value="documentsForm"
        @input="documentsForm = { ...documentsForm, ...$event }"
        @back="stage = 'iden'"
        @next="handleDocumentsNext"
      />
    </div>

    <div v-else-if="stage === 'send'" class="stage-wrapper">
      <Stepper :current-step="4" @step-click="jumpToStep" />
      <RegisterSend @back="stage = 'documents'" @submit="handleSubmit" />
    </div>
  </v-container>
</template>

<script>
import Stepper from "@/components/RegisterSeller/Stepper.vue";
import RegisterShop from "@/components/RegisterSeller/RegisterShop.vue";
import RegisterInfo from "@/components/RegisterSeller/RegisterInfo.vue";
import RegisterIdenCard from "@/components/RegisterSeller/RegisterIdenCard.vue";
import RegisterDocuments from "@/components/RegisterSeller/RegisterDocuments.vue";
import RegisterSend from "@/components/RegisterSeller/RegisterSend.vue";

import { sellerRegisterApi } from "@/services/api/auth";
import swalAlert from "@/services/alert/swalServices";

export default {
  name: "RegisterSeller",
  components: {
    Stepper,
    RegisterShop,
    RegisterInfo,
    RegisterIdenCard,
    RegisterDocuments,
    RegisterSend,
  },
  data() {
    return {
      isLoading: false,
      stage: "welcome",
      infoForm: {},
      idenForm: {},
      documentsForm: {},
    };
  },
  methods: {
    handleBackFromWelcome() {
      this.$router.push({ name: "login_seller" });
    },
    goInfo() {
      this.stage = "info";
    },
    jumpToStep(step) {
      const map = { 1: "info", 2: "iden", 3: "documents", 4: "send" };
      if (map[step]) this.stage = map[step];
    },
    handleInfoNext(payload) {
      this.infoForm = payload;
      this.stage = "iden";
    },
    handleIdenNext(payload) {
      this.idenForm = payload;
      this.stage = "documents";
    },
    handleDocumentsNext(payload) {
      this.documentsForm = payload;
      this.stage = "send";
    },

    toISODate(d) {
      if (!d) return "";
      const x = new Date(d);
      if (isNaN(x)) return "";
      const yyyy = x.getFullYear();
      const mm = String(x.getMonth() + 1).padStart(2, "0");
      const dd = String(x.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    async handleSubmit() {
      try {
        this.isLoading = true;

        const i = this.infoForm || {};
        const k = this.idenForm || {};
        const d = this.documentsForm || {};

        if (i.password !== i.password2) {
          await swalAlert.Error("รหัสผ่านไม่ตรงกัน");
          return;
        }

        // birthDate จาก RegisterIdenCard (day, monthNameTH, year)
        const birthDate = (() => {
          const { day, month, year } = k.birth || {};
          if (!day || !month || !year) return "";
          const months = [
            "มกราคม",
            "กุมภาพันธ์",
            "มีนาคม",
            "เมษายน",
            "พฤษภาคม",
            "มิถุนายน",
            "กรกฎาคม",
            "สิงหาคม",
            "กันยายน",
            "ตุลาคม",
            "พฤศจิกายน",
            "ธันวาคม",
          ];
          const idx = months.indexOf(month);
          if (idx < 0) return "";
          const mm = String(idx + 1).padStart(2, "0");
          const dd = String(day).padStart(2, "0");
          return `${year}-${mm}-${dd}`;
        })();

        // สร้าง FormData สำหรับส่งไฟล์
        const formData = new FormData();

        // ข้อมูลร้านค้า
        formData.append("shopName", i.shopName || "");
        formData.append("username", i.username || "");
        formData.append("subdistrictId", i.subdistrictId || "");
        formData.append("addressDetail", i.addressDetail || "");
        formData.append("phone", i.phone || "");
        formData.append("email", (i.email || "").trim());
        formData.append("password", i.password || "");
        formData.append("bankAccountNumber", i.accountNumber || "");
        formData.append("bankName", i.bankName || "");
        formData.append("promptPayId", i.promptPayId || "");
        formData.append("inviteCode", (i.refCode || "").toUpperCase());
        formData.append("latitude", i.lat || "");
        formData.append("longitude", i.lng || "");

        // ข้อมูลส่วนตัว
        formData.append("personalInfo[nationalId]", k.citizenId || "");
        formData.append("personalInfo[prefix]", k.prefix || "");
        formData.append("personalInfo[firstName]", k.firstName || "");
        formData.append("personalInfo[lastName]", k.lastName || "");
        formData.append("personalInfo[firstNameEn]", k.firstNameEn || "");
        formData.append("personalInfo[lastNameEn]", k.lastNameEn || "");
        formData.append("personalInfo[birthDate]", birthDate);
        formData.append("personalInfo[idIssueDate]", this.toISODate(k.issueDate) || "");
        formData.append("personalInfo[idExpireDate]", this.toISODate(k.expireDate) || "");

        // ไฟล์เอกสาร
        if (d.bankBookFile) {
          formData.append("bankBookDocument", d.bankBookFile);
        }
        if (d.idCardFile) {
          formData.append("idCardDocument", d.idCardFile);
        }
        formData.append("verifyOption", d.verifyOption || "");
        if (d.verifyOption === "1") {
          if (d.businessLicenseFile) formData.append("businessLicenseDocument", d.businessLicenseFile);
          if (d.storePhotoFile) formData.append("storePhotoDocument", d.storePhotoFile);
        } else if (d.verifyOption === "2") {
          if (d.halfBodyPhotoFile) formData.append("halfBodyPhotoDocument", d.halfBodyPhotoFile);
          if (d.fullBodyPhotoFile) formData.append("fullBodyPhotoDocument", d.fullBodyPhotoFile);
        }

        await sellerRegisterApi(formData);

        await swalAlert.Success("ลงทะเบียนร้านค้าสำเร็จ");
        this.$router.push({ name: "login_seller" });
      } catch (err) {
        const s = err?.response?.status;
        const msg = err?.response?.data?.message || "เกิดข้อผิดพลาด";
        if (s === 400)
          await swalAlert.Error(msg || "กรุณากรอกข้อมูลให้ครบถ้วน");
        else if (s === 401) await swalAlert.Error("กรุณาเข้าสู่ระบบก่อน");
        else if (s === 409) await swalAlert.Error(msg || "ข้อมูลนี้ถูกใช้งานแล้ว");
        else await swalAlert.Error(`ส่งแบบฟอร์มไม่สำเร็จ: ${msg}`);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0;
  padding: 0;
  min-height: 100vh;
}
.stage-wrapper {
  width: 100%;
}
@media (max-width: 599px) {
  .login-container { align-items: flex-start; }
}
</style>
